import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Book, Zap, Users, Clock, BarChart, MessageSquare, Shield, ChevronRight, CheckCircle, PlayCircle, FileText, Video, Download, ExternalLink, ChevronDown } from 'lucide-react';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const DocumentationPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSection, setSelectedSection] = useState('getting-started');
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [activeTab, setActiveTab] = useState('guides');

  // Video tutorials data
  const videoTutorials = [
    {
      id: 1,
      title: 'Getting Started with Shiftry - Complete Overview',
      description: 'Learn how to set up your account, invite team members, and create your first schedule in under 10 minutes.',
      thumbnail: '/Schedule Page.png',
      duration: '9:45',
      category: 'getting-started',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ' // Replace with actual video URLs
    },
    {
      id: 2,
      title: 'Creating and Managing Schedules',
      description: 'Master the art of creating efficient schedules with our AI-powered scheduling tools.',
      thumbnail: '/Schedule Page.png',
      duration: '12:30',
      category: 'scheduling',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ'
    },
    {
      id: 3,
      title: 'Time Tracking & GPS Clock-In Setup',
      description: 'Configure GPS-based clock-in/out and geofencing for accurate time tracking.',
      thumbnail: '/Timesheet Page.png',
      duration: '8:20',
      category: 'time-tracking',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ'
    },
    {
      id: 4,
      title: 'Analytics Dashboard Deep Dive',
      description: 'Unlock powerful insights with our comprehensive analytics and reporting tools.',
      thumbnail: '/Analyticspage.png',
      duration: '15:10',
      category: 'analytics',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ'
    },
    {
      id: 5,
      title: 'Team Management & Permissions',
      description: 'Learn how to organize your team, set roles, and manage permissions effectively.',
      thumbnail: '/Admin Dashboard.png',
      duration: '10:55',
      category: 'team-management',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ'
    },
    {
      id: 6,
      title: 'Mobile App Tutorial for Employees',
      description: 'A complete guide for employees on using the Shiftry mobile app for daily tasks.',
      thumbnail: '/Newsfeed.png',
      duration: '7:30',
      category: 'getting-started',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ'
    }
  ];

  // Detailed guides with step-by-step instructions
  const detailedGuides = {
    'getting-started': {
      sections: [
        {
          title: '1. Create Your Shiftry Account',
          steps: [
            'Visit shiftry.com and click "Start Free Trial"',
            'Enter your business details (company name, industry, team size)',
            'Verify your email address',
            'Complete the onboarding questionnaire',
            'You\'re all set! Your 14-day free trial begins immediately'
          ],
          tips: [
            'Choose a strong password with at least 12 characters',
            'Use your work email for better team organization',
            'Select the correct industry for tailored features'
          ]
        },
        {
          title: '2. Invite Your Team Members',
          steps: [
            'Navigate to "Team" section in the sidebar',
            'Click "Add Employee" button',
            'Enter employee details (name, email, phone, role)',
            'Assign department and location',
            'Click "Send Invitation" - they\'ll receive an email to join',
            'Bulk upload: Use "Import CSV" to add multiple employees at once'
          ],
          tips: [
            'Download the CSV template for bulk uploads',
            'Assign roles carefully - admins have full access',
            'Set up departments before adding employees for easier organization'
          ]
        },
        {
          title: '3. Set Up Your First Location',
          steps: [
            'Go to "Locations" in the admin dashboard',
            'Click "Add New Location"',
            'Enter location name and full address',
            'Set geofence radius (recommended: 100-300 meters)',
            'Configure location-specific settings (timezone, currency)',
            'Save and activate the location'
          ],
          tips: [
            'Use precise addresses for accurate GPS tracking',
            'Test geofence radius with a test employee',
            'Different locations can have different Employee rules'
          ]
        },
        {
          title: '4. Create Your First Schedule',
          steps: [
            'Navigate to "Schedule" tab',
            'Select the week you want to schedule',
            'Click on a day/time slot to create a shift',
            'Select employee(s) for the shift',
            'Set shift time, break duration, and notes',
            'Click "Publish Schedule" to notify employees',
            'Use "AI Auto-Schedule" for automatic optimization'
          ],
          tips: [
            'Use templates to save time on recurring schedules',
            'Color-code shifts by department for easy visualization',
            'Publish schedules at least 2 weeks in advance when possible'
          ]
        }
      ]
    },
    'scheduling': {
      sections: [
        {
          title: 'Understanding Schedule Templates',
          steps: [
            'Create a template from an existing schedule',
            'Name your template (e.g., "Weekend Rotation", "Holiday Coverage")',
            'Templates can be applied to any week with one click',
            'Modify template instances without affecting the original',
            'Share templates across multiple locations'
          ],
          tips: [
            'Create templates for recurring patterns',
            'Use different templates for peak vs. slow periods',
            'Templates adapt to employee availability automatically'
          ]
        },
        {
          title: 'AI-Powered Auto-Scheduling',
          steps: [
            'Click "AI Auto-Schedule" button on any week',
            'Select optimization goals (minimize cost, balance hours, respect preferences)',
            'Set constraints (max hours per employee, required skills)',
            'Review AI-generated schedule',
            'Make manual adjustments if needed',
            'Publish when satisfied'
          ],
          tips: [
            'AI learns from your scheduling patterns over time',
            'Combine AI suggestions with manual adjustments',
            'AI considers employee certifications and skill levels'
          ]
        },
        {
          title: 'Managing Shift Swaps & Trades',
          steps: [
            'Employee requests shift swap in mobile app',
            'Manager receives notification',
            'Review swap request in "Pending Requests"',
            'Check if replacement employee meets requirements',
            'Approve or deny with optional note',
            'Both employees are notified immediately'
          ],
          tips: [
            'Set approval rules to auto-approve certain swaps',
            'Enable peer-to-peer swap requests to reduce admin work',
            'Track swap history for payroll accuracy'
          ]
        },
        {
          title: 'Open Shifts & Self-Scheduling',
          steps: [
            'Create an "Open Shift" without assigning an employee',
            'Set who can claim it (all employees, specific department, etc.)',
            'Employees see open shifts in their mobile app',
            'First qualified employee to claim gets the shift',
            'You approve or auto-approve based on your settings',
            'Shift appears on schedule once confirmed'
          ],
          tips: [
            'Use open shifts to reduce last-minute coverage scrambles',
            'Set qualifications to ensure only qualified employees can claim',
            'Open shifts can help balance hours across your team'
          ]
        }
      ]
    },
    'time-tracking': {
      sections: [
        {
          title: 'GPS Clock-In Configuration',
          steps: [
            'Enable GPS tracking in Settings > Time & Attendance',
            'Set geofence radius for each location',
            'Choose enforcement level (strict, flexible, or disabled)',
            'Configure clock-in reminder notifications',
            'Test with a sample employee before full rollout',
            'Monitor GPS accuracy in Analytics dashboard'
          ],
          tips: [
            'Use flexible mode during initial rollout',
            'Larger geofence for outdoor worksites',
            'GPS works best with Wi-Fi + cellular enabled'
          ]
        },
        {
          title: 'Timesheet Approval Workflow',
          steps: [
            'Review weekly timesheets in "Timesheets" tab',
            'Check for anomalies (overtime, missed punches, GPS issues)',
            'Edit individual entries if needed',
            'Add notes for any manual adjustments',
            'Approve timesheets one-by-one or in bulk',
            'Export approved timesheets to payroll',
            'Locked timesheets cannot be edited by employees'
          ],
          tips: [
            'Set up approval reminders to avoid delays',
            'Use filters to review by department',
            'Enable 2-factor approval for large payrolls'
          ]
        },
        {
          title: 'Break Management',
          steps: [
            'Configure break rules in Settings (e.g., 30 min for 6+ hour shifts)',
            'Employees clock in/out for breaks in mobile app',
            'Automatic break deduction if configured',
            'View break compliance in reports',
            'Alerts for missed or short breaks',
            'Ensure Employee law compliance with break reminders'
          ],
          tips: [
            'Different break rules for different departments',
            'Paid vs. unpaid breaks are tracked separately',
            'Break reminders help maintain compliance'
          ]
        },
        {
          title: 'Overtime Rules & Alerts',
          steps: [
            'Define overtime thresholds (e.g., >40 hours/week)',
            'Set overtime multipliers (1.5x, 2x)',
            'Configure overtime alerts for managers',
            'View projected overtime in schedule view',
            'Overtime auto-calculated in timesheets',
            'Export overtime reports for payroll'
          ],
          tips: [
            'Different OT rules for exempt vs. non-exempt',
            'Daily + weekly overtime rules supported',
            'Alerts help prevent unplanned overtime costs'
          ]
        }
      ]
    },
    'analytics': {
      sections: [
        {
          title: 'Dashboard Overview',
          steps: [
            'Access Analytics from the main navigation',
            'View key metrics: Employee cost, hours worked, attendance rate',
            'Customize dashboard widgets',
            'Set date range for analysis',
            'Compare periods (this week vs. last week)',
            'Pin important metrics for quick access'
          ],
          tips: [
            'Daily check-ins keep you informed',
            'Set up alerts for unusual patterns',
            'Dashboard adapts based on your role'
          ]
        },
        {
          title: 'Creating Custom Reports',
          steps: [
            'Go to Reports > Create New Report',
            'Select report type (Employee cost, attendance, schedule adherence)',
            'Choose dimensions (by employee, department, location, date)',
            'Add filters and groupings',
            'Preview report before saving',
            'Save as template for reuse',
            'Schedule automatic email delivery'
          ],
          tips: [
            'Start with pre-built templates',
            'Save frequently used reports',
            'Export to Excel for deeper analysis'
          ]
        },
        {
          title: 'Employee Cost Analysis',
          steps: [
            'View real-time Employee cost in schedule view',
            'Compare actual vs. budgeted costs',
            'Break down by department, location, or employee',
            'Identify cost drivers and inefficiencies',
            'Forecast future costs based on schedules',
            'Set budget alerts to stay on track'
          ],
          tips: [
            'Include all Employee costs (wages, taxes, benefits)',
            'Track Employee as % of revenue',
            'Use forecasts to make scheduling decisions'
          ]
        }
      ]
    },
    'team-management': {
      sections: [
        {
          title: 'Adding and Organizing Employees',
          steps: [
            'Navigate to Team > Add Employee',
            'Fill in personal details (name, email, phone)',
            'Assign role (employee, manager, admin)',
            'Set department and location',
            'Add certifications and skills',
            'Set hourly rate or salary',
            'Configure notification preferences',
            'Send welcome email with login credentials'
          ],
          tips: [
            'Use employee ID for payroll integration',
            'Add profile photo for easy recognition',
            'Keep emergency contact information updated'
          ]
        },
        {
          title: 'Roles & Permissions System',
          steps: [
            'Three main roles: Employee, Manager, Admin',
            'Employees: view schedule, clock in/out, request time off',
            'Managers: create schedules, approve requests, view reports',
            'Admins: full system access, billing, settings',
            'Create custom roles for specific needs',
            'Assign multiple roles to one person',
            'Permissions can be location-specific'
          ],
          tips: [
            'Start with standard roles',
            'Limit admin access to trusted personnel',
            'Review permissions quarterly'
          ]
        },
        {
          title: 'Department Organization',
          steps: [
            'Go to Settings > Departments',
            'Create departments (e.g., Kitchen, Front-of-House, Management)',
            'Assign department heads',
            'Set department-specific settings',
            'Assign employees to departments',
            'View department performance in analytics',
            'Schedule by department for efficiency'
          ],
          tips: [
            'Align departments with your org structure',
            'Departments can have different Employee rules',
            'Use for cost center analysis'
          ]
        }
      ]
    },
    'communication': {
      sections: [
        {
          title: 'Team Messaging Best Practices',
          steps: [
            'Access messaging from the navigation bar',
            'Create group chats by department or location',
            'Send direct messages to individuals',
            'Share files, images, and links',
            'Use @mentions to notify specific people',
            'Search message history',
            'Enable/disable read receipts'
          ],
          tips: [
            'Create channels for different topics',
            'Use announcements for important updates',
            'Messages are encrypted for privacy'
          ]
        },
        {
          title: 'Announcements and Notifications',
          steps: [
            'Create announcements for all employees or specific groups',
            'Schedule announcements for future delivery',
            'Attach files or links to announcements',
            'Track who has read the announcement',
            'Send reminders to those who haven\'t read',
            'Archive old announcements',
            'Pin important announcements'
          ],
          tips: [
            'Keep announcements concise',
            'Use for policy changes, reminders, celebrations',
            'Schedule announcements during work hours'
          ]
        }
      ]
    }
  };

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Zap,
      articles: [
        { title: 'Quick Start Guide', time: '5 min' },
        { title: 'Creating Your Account', time: '3 min' },
        { title: 'Inviting Team Members', time: '4 min' },
        { title: 'Initial Setup Wizard', time: '8 min' }
      ]
    },
    {
      id: 'scheduling',
      title: 'Scheduling',
      icon: Clock,
      articles: [
        { title: 'Creating Your First Schedule', time: '6 min' },
        { title: 'Using Schedule Templates', time: '5 min' },
        { title: 'AI Auto-Scheduling', time: '7 min' },
        { title: 'Shift Swapping & Trades', time: '4 min' },
        { title: 'Open Shifts Management', time: '5 min' }
      ]
    },
    {
      id: 'time-tracking',
      title: 'Time & Attendance',
      icon: CheckCircle,
      articles: [
        { title: 'GPS Clock-In Setup', time: '6 min' },
        { title: 'Geofencing Configuration', time: '5 min' },
        { title: 'Timesheet Approval Workflow', time: '7 min' },
        { title: 'Break Management', time: '4 min' },
        { title: 'Overtime Rules', time: '6 min' }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Reports',
      icon: BarChart,
      articles: [
        { title: 'Dashboard Overview', time: '5 min' },
        { title: 'Creating Custom Reports', time: '8 min' },
        { title: 'Employee Cost Analysis', time: '7 min' },
        { title: 'Exporting Data', time: '4 min' },
        { title: 'Scheduled Reports', time: '5 min' }
      ]
    },
    {
      id: 'team-management',
      title: 'Team Management',
      icon: Users,
      articles: [
        { title: 'Adding Employees', time: '4 min' },
        { title: 'Roles & Permissions', time: '6 min' },
        { title: 'Department Organization', time: '5 min' },
        { title: 'Location Management', time: '5 min' },
        { title: 'Certification Tracking', time: '6 min' }
      ]
    },
    {
      id: 'communication',
      title: 'Communication',
      icon: MessageSquare,
      articles: [
        { title: 'Team Messaging', time: '4 min' },
        { title: 'Announcements', time: '3 min' },
        { title: 'Notifications Settings', time: '5 min' },
        { title: 'File Sharing', time: '4 min' }
      ]
    },
    {
      id: 'integrations',
      title: 'Integrations',
      icon: Zap,
      articles: [
        { title: 'Payroll Integration', time: '8 min' },
        { title: 'API Documentation', time: '15 min' },
        { title: 'Webhooks Setup', time: '10 min' },
        { title: 'Third-Party Apps', time: '6 min' }
      ]
    },
    {
      id: 'security',
      title: 'Security & Compliance',
      icon: Shield,
      articles: [
        { title: 'Data Security Overview', time: '6 min' },
        { title: 'GDPR Compliance', time: '8 min' },
        { title: 'Employee Law Compliance', time: '10 min' },
        { title: 'Audit Logs', time: '5 min' }
      ]
    }
  ];

  const quickLinks = [
    {
      title: 'Video Tutorials',
      description: 'Watch step-by-step video guides',
      icon: Video,
      link: '#videos',
      action: () => setActiveTab('videos')
    },
    {
      title: 'User Guides',
      description: 'Detailed step-by-step documentation',
      icon: FileText,
      link: '#guides',
      action: () => setActiveTab('guides')
    },
    {
      title: 'API Reference',
      description: 'Complete API documentation for developers',
      icon: Zap,
      link: '#',
      external: true
    },
    {
      title: 'Download Resources',
      description: 'Templates, CSV samples, and guides',
      icon: Download,
      link: '#',
      external: true
    }
  ];

  // Filter videos based on selected section
  const filteredVideos = selectedSection === 'all' 
    ? videoTutorials 
    : videoTutorials.filter(video => video.category === selectedSection);

  // Get current section guides
  const currentGuides = detailedGuides[selectedSection] || { sections: [] };

  return (
    <>
      <LandingNav />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Book className="w-16 h-16 mx-auto mb-6" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Documentation
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-8">
              Everything you need to know about using Shiftry to manage your workforce effectively.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
                <input 
                  type="text"
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-14 pr-6 py-5 rounded-xl text-gray-900 text-lg focus:outline-none focus:ring-4 focus:ring-blue-300"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => {
              const IconComponent = link.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={link.action}
                  className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-100 hover:shadow-lg transition-all group cursor-pointer"
                >
                  <IconComponent className="w-10 h-10 text-blue-600 mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors flex items-center">
                    {link.title}
                    {link.external && <ExternalLink className="w-4 h-4 ml-2" />}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {link.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Documentation Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="mb-8 flex justify-center">
            <div className="bg-white rounded-xl shadow-md p-2 inline-flex">
              <button
                onClick={() => setActiveTab('guides')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center ${
                  activeTab === 'guides'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                <FileText className="w-5 h-5 mr-2" />
                Step-by-Step Guides
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center ${
                  activeTab === 'videos'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                <Video className="w-5 h-5 mr-2" />
                Video Tutorials
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Categories</h3>
                <nav className="space-y-2">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => setSelectedSection(section.id)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center ${
                          selectedSection === section.id
                            ? 'bg-blue-600 text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <Icon className="w-5 h-5 mr-3" />
                        <span className="font-medium">{section.title}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-3">
              {/* Video Tutorials Tab */}
              {activeTab === 'videos' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="bg-white rounded-2xl shadow-lg p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Video Tutorials</h2>
                    <p className="text-gray-600 mb-8">
                      Watch our comprehensive video guides to master Shiftry features quickly and efficiently.
                    </p>

                    <div className="grid md:grid-cols-2 gap-6">
                      {filteredVideos.map((video) => (
                        <div
                          key={video.id}
                          className="group cursor-pointer"
                        >
                          <div className="relative overflow-hidden rounded-xl mb-4">
                            <img
                              src={video.thumbnail}
                              alt={video.title}
                              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all flex items-center justify-center">
                              <div className="bg-white rounded-full p-4 group-hover:scale-110 transition-transform">
                                <PlayCircle className="w-8 h-8 text-blue-600" />
                              </div>
                            </div>
                            <div className="absolute bottom-3 right-3 bg-black/80 text-white text-sm px-3 py-1 rounded-lg">
                              {video.duration}
                            </div>
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                            {video.title}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {video.description}
                          </p>
                        </div>
                      ))}
                    </div>

                    {filteredVideos.length === 0 && (
                      <div className="text-center py-12">
                        <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No video tutorials available for this category yet.</p>
                        <p className="text-sm text-gray-400 mt-2">Check back soon for new content!</p>
                      </div>
                    )}
                  </div>

                  {/* Video Learning Tips */}
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-100">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">📚 Learning Tips</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Watch videos at your own pace - pause and replay as needed</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Follow along in your Shiftry account for hands-on learning</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Enable subtitles for better understanding</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Start with "Getting Started" videos if you're new to Shiftry</span>
                      </li>
                    </ul>
                  </div>
                </motion.div>
              )}

              {/* Step-by-Step Guides Tab */}
              {activeTab === 'guides' && sections.map((section) => {
                const Icon = section.icon;
                return selectedSection === section.id ? (
                  <motion.div
                    key={section.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="bg-white rounded-2xl shadow-lg p-8"
                  >
                    <div className="flex items-center mb-8">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold text-gray-900">{section.title}</h2>
                        <p className="text-gray-600 mt-1">Complete guide with step-by-step instructions</p>
                      </div>
                    </div>

                    {/* Article List */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Access Articles</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {section.articles.map((article, index) => (
                          <a
                            key={index}
                            href="#"
                            className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h4 className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                  {article.title}
                                </h4>
                                <p className="text-sm text-gray-500 mt-1">{article.time} read</p>
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* Detailed Step-by-Step Guides */}
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">📖 Detailed Guides</h3>
                      
                      {currentGuides.sections.map((guide, guideIndex) => (
                        <div key={guideIndex} className="border border-gray-200 rounded-xl overflow-hidden">
                          <button
                            onClick={() => setExpandedArticle(expandedArticle === guideIndex ? null : guideIndex)}
                            className="w-full p-6 bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 transition-all flex items-center justify-between"
                          >
                            <h4 className="text-xl font-bold text-gray-900">{guide.title}</h4>
                            <ChevronDown className={`w-6 h-6 text-blue-600 transition-transform ${
                              expandedArticle === guideIndex ? 'rotate-180' : ''
                            }`} />
                          </button>
                          
                          {expandedArticle === guideIndex && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="p-6 bg-white"
                            >
                              <div className="mb-6">
                                <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                  <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">
                                    ✓
                                  </span>
                                  Step-by-Step Instructions
                                </h5>
                                <ol className="space-y-3">
                                  {guide.steps.map((step, stepIndex) => (
                                    <li key={stepIndex} className="flex items-start">
                                      <span className="bg-blue-100 text-blue-600 rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0 mt-0.5">
                                        {stepIndex + 1}
                                      </span>
                                      <span className="text-gray-700 pt-0.5">{step}</span>
                                    </li>
                                  ))}
                                </ol>
                              </div>

                              {guide.tips && guide.tips.length > 0 && (
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                                  <h5 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                    <span className="text-2xl mr-2">💡</span>
                                    Pro Tips
                                  </h5>
                                  <ul className="space-y-2">
                                    {guide.tips.map((tip, tipIndex) => (
                                      <li key={tipIndex} className="flex items-start">
                                        <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                                        <span className="text-gray-700 text-sm">{tip}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </motion.div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Example Screenshots */}
                    <div className="mt-8 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">📸 Interface Preview</h3>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <img 
                          src={
                            section.id === 'scheduling' ? '/Schedule Page.png' :
                            section.id === 'time-tracking' ? '/Timesheet Page.png' :
                            section.id === 'analytics' ? '/Analyticspage.png' :
                            section.id === 'team-management' ? '/Admin Dashboard.png' :
                            section.id === 'communication' ? '/Newsfeed.png' :
                            '/AI page.png'
                          }
                          alt={`${section.title} Interface`}
                          className="w-full h-auto rounded-lg shadow-lg"
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-4 text-center">
                        Screenshot of the {section.title} interface in Shiftry
                      </p>
                    </div>
                  </motion.div>
                ) : null;
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Help CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Still Need Help?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Our support team is available 24/7 to assist you with any questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all">
              Contact Support
            </button>
            <button className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all">
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default DocumentationPage;
